import React, { useState, useRef, useEffect } from 'react';
import { Clock } from 'lucide-react';

interface TimePickerProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
}

function pad(n: number) {
  return n < 10 ? `0${n}` : String(n);
}

// Generate times in 30-min increments (00:00 – 23:30)
const ALL_TIMES: string[] = [];
for (let h = 0; h < 24; h++) {
  ALL_TIMES.push(`${pad(h)}:00`);
  ALL_TIMES.push(`${pad(h)}:30`);
}

function formatDisplay(time24: string): string {
  if (!time24) return '';
  const [hStr, mStr] = time24.split(':');
  let h = parseInt(hStr, 10);
  const m = mStr || '00';
  const ampm = h >= 12 ? 'PM' : 'AM';
  if (h === 0) h = 12;
  else if (h > 12) h -= 12;
  return `${h}:${m} ${ampm}`;
}

export default function TimePicker({ value, onChange, placeholder = 'Select time', className }: TimePickerProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Scroll to selected time when opening
  useEffect(() => {
    if (open && listRef.current && value) {
      const idx = ALL_TIMES.indexOf(value);
      if (idx >= 0) {
        const el = listRef.current.children[idx] as HTMLElement;
        if (el) el.scrollIntoView({ block: 'center' });
      }
    }
  }, [open, value]);

  const select = (t: string) => {
    onChange(t);
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative">
      {/* Trigger */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className={className || 'w-full bg-fieldiq-dark rounded-lg px-3 py-2.5 text-sm border border-fieldiq-border focus:border-fieldiq-accent outline-none flex items-center justify-between gap-2 text-left'}
      >
        <span className={value ? 'text-white' : 'text-fieldiq-muted'}>
          {value ? formatDisplay(value) : placeholder}
        </span>
        <Clock size={14} className="text-fieldiq-muted flex-shrink-0" />
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute z-50 mt-1 left-0 w-full bg-fieldiq-card border border-fieldiq-border rounded-xl shadow-2xl overflow-hidden animate-fade-in">
          {/* Quick presets row */}
          <div className="flex flex-wrap gap-1 p-2 border-b border-fieldiq-border">
            {['06:00', '07:00', '08:00', '12:00', '17:00', '18:00'].map(t => (
              <button
                key={t}
                type="button"
                onClick={() => select(t)}
                className={`px-2 py-1 rounded-lg text-xs font-medium transition-colors ${
                  value === t
                    ? 'bg-fieldiq-accent text-fieldiq-dark'
                    : 'bg-fieldiq-dark text-white hover:bg-fieldiq-border'
                }`}
              >
                {formatDisplay(t)}
              </button>
            ))}
          </div>

          {/* Scrollable time list */}
          <div ref={listRef} className="max-h-48 overflow-y-auto">
            {ALL_TIMES.map(t => {
              const isSelected = t === value;
              const isHour = t.endsWith(':00');
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => select(t)}
                  className={`w-full px-3 py-2 text-left text-sm transition-colors ${
                    isSelected
                      ? 'bg-fieldiq-accent text-fieldiq-dark font-bold'
                      : isHour
                        ? 'text-white hover:bg-fieldiq-border font-medium'
                        : 'text-fieldiq-muted hover:bg-fieldiq-border'
                  }`}
                >
                  {formatDisplay(t)}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
